import type { CatalogRepository } from './repository'
import type {
  AttributeKind,
  CatalogItem,
  CatalogSopFile,
} from './types'

const delay = (ms = 80) => new Promise((resolve) => setTimeout(resolve, ms))
const STORAGE_KEY = 'erp.catalog.v2'

function newId(): string {
  return crypto.randomUUID()
}

function seed(
  code: string,
  name: string,
  note = '',
  sopFiles: CatalogSopFile[] = [],
): CatalogItem {
  return {
    id: newId(),
    code: code.toUpperCase(),
    name,
    isActive: true,
    note,
    sopFiles,
  }
}

const defaultStores: Record<AttributeKind, CatalogItem[]> = {
  color: [
    seed('WAL', 'Walnut', 'Sơn PU màu Walnut — phun 2 lớp, bóng mờ.'),
    seed('NAT', 'Natural', 'Giữ màu gỗ tự nhiên, dầu wax.'),
    seed('BLK', 'Black'),
    seed('WHT', 'White'),
    seed('BRN', 'Brown', 'Brown matte — kiểm tra độ phủ cạnh.'),
    seed('OAKN', 'Oak Natural'),
  ],
  wood: [
    seed('OAK', 'Oak'),
    seed('CRS', 'Cao Su'),
    seed('WLN', 'Walnut'),
    seed('ASH', 'Ash'),
    seed('MDF', 'MDF'),
    seed('TRM', 'Tràm'),
  ],
  veneer: [
    seed('VNAT', 'Natural'),
    seed('VWO', 'White Oak'),
    seed('VWAL', 'Walnut'),
    seed('VASH', 'Ash'),
    seed('VMEL', 'Melamine'),
    seed('VOAK', 'Oak'),
  ],
}

type LegacyCatalogItem = CatalogItem & {
  sopFile?: CatalogSopFile & { id?: string }
}

function normalizeItem(item: LegacyCatalogItem): CatalogItem {
  const legacy = item.sopFile
  let sopFiles = Array.isArray(item.sopFiles) ? item.sopFiles : []
  if (!sopFiles.length && legacy?.dataUrl) {
    sopFiles = [
      {
        id: legacy.id ?? newId(),
        name: legacy.name,
        dataUrl: legacy.dataUrl,
        uploadedAt: legacy.uploadedAt ?? new Date().toISOString(),
        size: legacy.size,
      },
    ]
  }
  return {
    id: item.id,
    code: item.code,
    name: item.name,
    isActive: item.isActive,
    note: item.note ?? '',
    sopFiles,
  }
}

function loadStores(): Record<AttributeKind, CatalogItem[]> {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) ??
      localStorage.getItem('erp.catalog.v1')
    if (!raw) return structuredClone(defaultStores)
    const parsed = JSON.parse(raw) as Record<AttributeKind, LegacyCatalogItem[]>
    for (const kind of ['color', 'wood', 'veneer'] as AttributeKind[]) {
      if (!Array.isArray(parsed[kind])) return structuredClone(defaultStores)
      parsed[kind] = parsed[kind].map(normalizeItem)
    }
    return parsed as Record<AttributeKind, CatalogItem[]>
  } catch {
    return structuredClone(defaultStores)
  }
}

const stores = loadStores()

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stores))
  } catch {
    // ignore quota / private mode
  }
}

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ')
}

function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/\s+/g, '')
}

function normalizeNote(note: string | undefined): string {
  return (note ?? '').trim()
}

function assertUniqueName(kind: AttributeKind, name: string, exceptId?: string) {
  const key = name.toLowerCase()
  const exists = stores[kind].some(
    (item) => item.name.toLowerCase() === key && item.id !== exceptId,
  )
  if (exists) throw new Error(`Tên "${name}" đã tồn tại trong danh mục.`)
}

function assertUniqueCode(kind: AttributeKind, code: string, exceptId?: string) {
  const key = code.toLowerCase()
  const exists = stores[kind].some(
    (item) => item.code.toLowerCase() === key && item.id !== exceptId,
  )
  if (exists) throw new Error(`Mã "${code}" đã tồn tại trong danh mục.`)
}

export const mockCatalogRepository: CatalogRepository = {
  async list(kind, includeInactive = false) {
    await delay()
    return stores[kind]
      .filter((item) => includeInactive || item.isActive)
      .slice()
      .sort((a, b) => a.code.localeCompare(b.code, 'vi'))
  },

  async create(kind, input) {
    await delay()
    const name = normalizeName(input.name)
    const code = normalizeCode(input.code)
    if (!code) throw new Error('Mã nội bộ không được để trống.')
    if (!name) throw new Error('Tên không được để trống.')
    assertUniqueCode(kind, code)
    assertUniqueName(kind, name)
    const item: CatalogItem = {
      id: newId(),
      code,
      name,
      isActive: input.isActive ?? true,
      note: normalizeNote(input.note),
      sopFiles: input.sopFiles ?? [],
    }
    stores[kind] = [...stores[kind], item]
    persist()
    return item
  },

  async update(kind, id, input) {
    await delay()
    const index = stores[kind].findIndex((item) => item.id === id)
    if (index < 0) throw new Error('Không tìm thấy mục danh mục.')
    const current = stores[kind][index]
    const name =
      input.name !== undefined ? normalizeName(input.name) : current.name
    const code =
      input.code !== undefined ? normalizeCode(input.code) : current.code
    if (!code) throw new Error('Mã nội bộ không được để trống.')
    if (!name) throw new Error('Tên không được để trống.')
    assertUniqueCode(kind, code, id)
    assertUniqueName(kind, name, id)

    const updated: CatalogItem = {
      ...current,
      code,
      name,
      isActive: input.isActive ?? current.isActive,
      note:
        input.note !== undefined ? normalizeNote(input.note) : current.note,
      sopFiles:
        input.sopFiles !== undefined ? input.sopFiles : current.sopFiles,
    }
    stores[kind] = [
      ...stores[kind].slice(0, index),
      updated,
      ...stores[kind].slice(index + 1),
    ]
    persist()
    return updated
  },

  /** Soft-archive: deactivate. Never hard-delete master rows. */
  async remove(kind, id) {
    await delay()
    const index = stores[kind].findIndex((item) => item.id === id)
    if (index < 0) throw new Error('Không tìm thấy mục danh mục.')
    const current = stores[kind][index]
    stores[kind] = [
      ...stores[kind].slice(0, index),
      { ...current, isActive: false },
      ...stores[kind].slice(index + 1),
    ]
    persist()
  },
}

/** Resolve IDs to catalog rows (including inactive) for product display. */
export function resolveCatalogItems(
  kind: AttributeKind,
  ids: string[],
): CatalogItem[] {
  const map = new Map(stores[kind].map((item) => [item.id, item]))
  return ids
    .map((id) => map.get(id))
    .filter((item): item is CatalogItem => Boolean(item))
}

export function findCatalogIdByName(
  kind: AttributeKind,
  name: string,
): string | undefined {
  const key = name.trim().toLowerCase()
  return stores[kind].find((item) => item.name.toLowerCase() === key)?.id
}
